import React, { useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'

import { useParams, Routes, Route, useMatch, Navigate } from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { YStack, XStack } from 'tamagui'

import { GuildMetadata } from '@components'
import { H2, wrapSuspenseComponent, Link } from '@newComponents'
import { Group } from '@newComponents/Group'

// import { GuildEventsScreenUpcomingEventsCard } from './GuildEventsScreenUpcomingEventsCard'
// import { GuildEventsScreenPastEventsCard } from './GuildEventsScreenPastEventsCard'

import { GuildEventsScreenQuery } from './__generated__/GuildEventsScreenQuery.graphql'

const GuildEventsUpcomingScreen = wrapSuspenseComponent(
  loadable(
    () => import('../GuildEventsUpcomingScreen/GuildEventsUpcomingScreen')
  )
)

const GuildEventsPastScreen = wrapSuspenseComponent(
  loadable(() => import('../GuildEventsPastScreen/GuildEventsPastScreen'))
)

export const GuildEventsScreen = () => {
  const params = useParams()

  const upcomingMatch = useMatch(`${params.actorId}/events/upcoming`)
  const pastMatch = useMatch(`${params.actorId}/events/past`)

  const data = useLazyLoadQuery<GuildEventsScreenQuery>(
    graphql`
      query GuildEventsScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            name

            ...GuildMetadata_guild

            upcomingEventsCount: eventsByTimeSelection(
              first: 1
              timeSelection: UPCOMING
              orderBy: [START_AT_DESC, ID_ASC]
            ) {
              totalCount
            }

            pastEventsCount: eventsByTimeSelection(
              first: 1
              timeSelection: PAST
              orderBy: [START_AT_DESC, ID_ASC]
            ) {
              totalCount
            }
          }
        }
      }
    `,
    {
      id: params.actorId || '',
    }
  )

  const upcomingOrPast = useMemo(() => {
    if (upcomingMatch) {
      return 'upcoming'
    }

    if (pastMatch) {
      return 'past'
    }

    // Need to return something here for the type signature, but this condition will return early below
    if (data.nodeBySlugId?.__typename !== 'Guild') {
      return 'upcoming'
    }

    if (
      !data.nodeBySlugId.upcomingEventsCount.totalCount &&
      data.nodeBySlugId.pastEventsCount.totalCount
    ) {
      return 'past'
    }

    return 'upcoming'
  }, [upcomingMatch, pastMatch, data])

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  return (
    <>
      <GuildMetadata
        guild={data.nodeBySlugId}
        title={`${data.nodeBySlugId.name} ${
          upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'
        } Events`}
      />

      <YStack gap='$6' flex={1}>
        <XStack alignItems='center' gap='$4'>
          <XStack flex={1}>
            <H2>
              {upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'} Events
            </H2>
          </XStack>

          <Group activeIndex={upcomingOrPast === 'past' ? 1 : 0}>
            <XStack gap='$2'>
              <Group.Item index={0}>
                <Link
                  tag='a'
                  href={`/${params.actorId}/events/upcoming`}
                  size='$4'
                  fontWeight='$6'
                  color='$grey9'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  Upcoming
                </Link>
              </Group.Item>

              <Group.Item index={1}>
                <Link
                  href={`/${params.actorId}/events/past`}
                  size='$4'
                  fontWeight='$6'
                  color='$grey9'
                  hoverStyle={{
                    textDecorationLine: 'none',
                  }}
                  paddingVertical='$2'
                  paddingHorizontal='$3'
                >
                  Past
                </Link>
              </Group.Item>
            </XStack>
          </Group>
        </XStack>

        <Routes>
          {!data.nodeBySlugId.upcomingEventsCount.totalCount &&
          data.nodeBySlugId.pastEventsCount.totalCount ? (
            <Route index element={<GuildEventsPastScreen />} />
          ) : (
            <Route index element={<GuildEventsUpcomingScreen />} />
          )}

          <Route path='upcoming' element={<GuildEventsUpcomingScreen />} />

          <Route path='past' element={<GuildEventsPastScreen />} />

          <Route
            path='*'
            element={
              upcomingOrPast === 'past' ? (
                <Navigate to='past' replace />
              ) : (
                <Navigate to='upcoming' replace />
              )
            }
          />
        </Routes>
      </YStack>
    </>
  )
}

export default GuildEventsScreen
